import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Plus,
  Edit2,
  Save,
  X,
  Trash2,
  FolderTree,
  PlusCircle,
  MinusCircle
} from 'lucide-react';
import { useUser } from '../../providers/user';
import { googleSheetsService } from '../../services/google-sheets';
import { formatCurrency } from '../../utils/currency';

const STORAGE_KEY_ID = 'finance_categories_sheet';
const STORAGE_KEY_NAME = 'finance_categories_sheet_name';
const SPREADSHEET_TITLE = 'Finance Tracker Categories';
const DEFAULT_SHEET_NAME = 'Categories';

const makeId = () =>
  (typeof crypto !== 'undefined' && crypto.randomUUID) ? crypto.randomUUID() : `id-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

const CategoriesManager = ({ categories, setCategories, selectedYear, currency = 'USD', showCurrency = true, showAmounts = true }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [spreadsheetId, setSpreadsheetId] = useState(null);
  const [sheetName, setSheetName] = useState(DEFAULT_SHEET_NAME);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [error, setError] = useState(null);

  const { currentUser } = useUser();

  const [newCategory, setNewCategory] = useState({
    category: '',
    budget: '',
    subcategories: [{ subCategory: '' }]
  });

  const [editCategoryData, setEditCategoryData] = useState({
    budget: '',
    subcategories: [],
    newSubcategories: []
  });

  const hasInitRef = useRef(false);
  const createInProgressRef = useRef(false);

  const persistSheetInfo = (id, name) => {
    try {
      localStorage.setItem(STORAGE_KEY_ID, id);
      localStorage.setItem(STORAGE_KEY_NAME, name);
    } catch (e) {
      /* ignore */
    }
  };

  const safeVerifySpreadsheet = async (id, attempts = 2, delayMs = 700) => {
    for (let i = 0; i < attempts; i++) {
      try {
        const ok = await googleSheetsService.verifySpreadsheet(id);
        return !!ok;
      } catch (err) {
        if (i < attempts - 1) await new Promise(r => setTimeout(r, delayMs));
      }
    }
    return false;
  };

  const createNewCategoriesSheet = useCallback(async () => {
    if (createInProgressRef.current) return null;
    createInProgressRef.current = true;
    setIsLoading(true);
    setError(null);

    try {
      const sheetTitle = DEFAULT_SHEET_NAME;
      const created = await googleSheetsService.createSpreadsheet(SPREADSHEET_TITLE, sheetTitle);
      if (!created || !created.spreadsheetId) throw new Error('Failed to create spreadsheet');
      const newSpreadsheetId = created.spreadsheetId;
      setSpreadsheetId(newSpreadsheetId);
      setSheetName(sheetTitle);
      persistSheetInfo(newSpreadsheetId, sheetTitle);
      const headers = ['Category', 'Sub-Category', 'Budget'];
      await googleSheetsService.addHeaders(newSpreadsheetId, headers, sheetTitle);
      setCategories([]);
      return newSpreadsheetId;
    } catch (err) {
      setError(err?.message || 'Failed to create sheet');
      return null;
    } finally {
      createInProgressRef.current = false;
      setIsLoading(false);
    }
  }, [setCategories]);

  const loadCategories = useCallback(
    async (sheetId, sheet) => {
      setIsLoading(true);
      setError(null);
      try {
        if (!sheetId) throw new Error('No spreadsheetId provided to loadCategories');
        const data = await googleSheetsService.getSheetData(sheetId, 'A2:C', sheet);
        if (data?.values && data.values.length > 0) {
          const loaded = data.values.map((row, index) => ({
            id: makeId(),
            rowNumber: index + 2,
            category: row[0] || '',
            subCategory: row[1] || '',
            budget: parseFloat(row[2]) || 0
          }));
          setCategories(loaded);
        } else {
          setCategories([]);
        }
      } catch (err) {
        if (err?.message && err.message.includes('Unable to parse range')) {
          setCategories([]);
        } else {
          setError(err?.message || 'Failed to load categories');
        }
      } finally {
        setIsLoading(false);
      }
    },
    [setCategories]
  );

  const findOrCreateSpreadsheetByName = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const cachedId = localStorage.getItem(STORAGE_KEY_ID);
      const cachedName = localStorage.getItem(STORAGE_KEY_NAME) || DEFAULT_SHEET_NAME;

      if (cachedId) {
        const valid = await safeVerifySpreadsheet(cachedId);
        if (valid) {
          setSpreadsheetId(cachedId);
          setSheetName(cachedName);
          await loadCategories(cachedId, cachedName);
          setIsLoading(false);
          return;
        }
      }

      const found = await googleSheetsService.searchSpreadsheetByName(SPREADSHEET_TITLE);
      if (found && found.spreadsheetId) {
        setSpreadsheetId(found.spreadsheetId);
        setSheetName(found.sheetName || DEFAULT_SHEET_NAME);
        persistSheetInfo(found.spreadsheetId, found.sheetName || DEFAULT_SHEET_NAME);
        await loadCategories(found.spreadsheetId, found.sheetName || DEFAULT_SHEET_NAME);
        setIsLoading(false);
        return;
      }

      const createdId = await createNewCategoriesSheet();
      if (!createdId) throw new Error('Failed to create or find spreadsheet');
    } catch (err) {
      setError(err?.message || 'Initialization failed');
    } finally {
      setIsLoading(false);
    }
  }, [createNewCategoriesSheet, loadCategories]);

  useEffect(() => {
    if (!currentUser?.accessToken) return;
    if (hasInitRef.current) return;
    googleSheetsService.setAccessToken(currentUser.accessToken);
    hasInitRef.current = true;
    findOrCreateSpreadsheetByName();
  }, [currentUser?.accessToken, findOrCreateSpreadsheetByName]);

  const handleAddCategory = async () => {
    if (!newCategory.category.trim()) {
      alert('Please enter a category name');
      return;
    }

    // Budget is now optional - no validation needed

    const grouped = (categories || []).reduce((acc, c) => {
      if (!acc[c.category]) acc[c.category] = [];
      acc[c.category].push(c);
      return acc;
    }, {});
    const normalized = newCategory.category.trim().toLowerCase();
    if (Object.keys(grouped).some(c => c.toLowerCase() === normalized)) {
      alert(`Category "${newCategory.category}" already exists! Please use the edit button to add subcategories to existing categories.`);
      return;
    }

    const validSubcategories = newCategory.subcategories.filter(s => s.subCategory.trim());
    if (validSubcategories.length === 0) {
      alert('Please add at least one valid subcategory');
      return;
    }

    if (!spreadsheetId) {
      await findOrCreateSpreadsheetByName();
      if (!spreadsheetId) {
        alert('Unable to add category: no spreadsheet available');
        return;
      }
    }

    try {
      const added = [];
      const budget = parseFloat(newCategory.budget) || 0; // Default to 0 if empty

      for (const sub of validSubcategories) {
        const values = [newCategory.category.trim(), sub.subCategory.trim(), budget];
        const appendRes = await googleSheetsService.appendRow(spreadsheetId, values, sheetName);
        let rowNumber = null;
        if (appendRes?.updatedRange) {
          const m = appendRes.updatedRange.match(/!A(\d+):C(\d+)/);
          if (m && m[2]) rowNumber = parseInt(m[2], 10);
        }
        if (!rowNumber) {
          const last = await googleSheetsService.getSheetLastRow(spreadsheetId, sheetName);
          rowNumber = last;
        }
        const item = {
          id: makeId(),
          rowNumber,
          category: values[0],
          subCategory: values[1],
          budget: values[2]
        };
        added.push(item);
      }
      setCategories(prev => [...added, ...(prev || [])]);
      setNewCategory({ category: '', budget: '', subcategories: [{ subCategory: '' }] });
      setShowAddForm(false);
    } catch (err) {
      alert('Failed to add category: ' + (err?.message || err));
    }
  };

  const handleEditCategoryStart = (categoryName) => {
    const grouped = (categories || []).reduce((acc, cat) => {
      if (!acc[cat.category]) acc[cat.category] = [];
      acc[cat.category].push(cat);
      return acc;
    }, {});
    const categorySubs = grouped[categoryName] || [];
    const categoryBudget = categorySubs.length > 0 ? categorySubs[0].budget : 0;

    setEditingCategory(categoryName);
    setEditCategoryData({
      budget: categoryBudget.toString(),
      subcategories: categorySubs.map(cat => ({
        id: cat.id,
        rowNumber: cat.rowNumber,
        subCategory: cat.subCategory
      })),
      newSubcategories: []
    });
  };

  const addEditSubcategoryField = () => {
    setEditCategoryData(prev => ({ ...prev, newSubcategories: [...(prev.newSubcategories || []), { subCategory: '' }] }));
  };

  const removeEditSubcategoryField = (index) => {
    setEditCategoryData(prev => ({ ...prev, newSubcategories: prev.newSubcategories.filter((_, i) => i !== index) }));
  };

  const updateEditSubcategory = (index, field, value) => {
    setEditCategoryData(prev => ({ ...prev, subcategories: prev.subcategories.map((s, i) => i === index ? { ...s, [field]: value } : s) }));
  };

  const updateNewEditSubcategory = (index, field, value) => {
    setEditCategoryData(prev => ({ ...prev, newSubcategories: prev.newSubcategories.map((s, i) => i === index ? { ...s, [field]: value } : s) }));
  };

  const handleSaveCategoryEdit = async () => {
    if (!spreadsheetId) {
      alert('No spreadsheet connected');
      return;
    }

    // Budget is now optional - no validation needed

    try {
      const budget = parseFloat(editCategoryData.budget) || 0; // Default to 0 if empty

      // Update existing subcategories
      for (let i = 0; i < editCategoryData.subcategories.length; i++) {
        const sub = editCategoryData.subcategories[i];
        if (!sub.subCategory.trim()) {
          alert('All subcategories must have a name');
          return;
        }
        if (!sub.rowNumber) continue;
        const range = `A${sub.rowNumber}:C${sub.rowNumber}`;
        await googleSheetsService.updateRange(spreadsheetId, range, [[editingCategory, sub.subCategory.trim(), budget]], sheetName);
        setCategories(prev => prev.map(cat => cat.id === sub.id ? { ...cat, subCategory: sub.subCategory.trim(), budget } : cat));
      }

      // Add new subcategories
      if (editCategoryData.newSubcategories && editCategoryData.newSubcategories.length > 0) {
        const validNew = editCategoryData.newSubcategories.filter(s => s.subCategory.trim());
        for (const sub of validNew) {
          const values = [editingCategory, sub.subCategory.trim(), budget];
          const appendRes = await googleSheetsService.appendRow(spreadsheetId, values, sheetName);
          let rowNumber = null;
          if (appendRes?.updatedRange) {
            const m = appendRes.updatedRange.match(/!A(\d+):C(\d+)/);
            if (m && m[2]) rowNumber = parseInt(m[2], 10);
          }
          if (!rowNumber) {
            const last = await googleSheetsService.getSheetLastRow(spreadsheetId, sheetName);
            rowNumber = last;
          }
          const newItem = { id: makeId(), rowNumber, category: editingCategory, subCategory: values[1], budget: values[2] };
          setCategories(prev => [...(prev || []), newItem]);
        }
      }

      setEditingCategory(null);
      setEditCategoryData({ budget: '', subcategories: [], newSubcategories: [] });
    } catch (err) {
      alert('Failed to save changes: ' + (err?.message || err));
    }
  };

  const handleDelete = async (id) => {
    // eslint-disable-next-line no-restricted-globals
    if (!confirm('Are you sure you want to delete this subcategory?')) return;
    if (!spreadsheetId) {
      alert('No spreadsheet connected');
      return;
    }
    try {
      const cat = categories.find(c => c.id === id);
      if (!cat || !cat.rowNumber) return;
      const targetRow = cat.rowNumber;
      setCategories(prev => prev.filter(c => c.id !== id));
      const sheetMeta = await googleSheetsService.getSpreadsheetMetadata(spreadsheetId);
      const targetSheet = sheetMeta.sheets.find(s => s.properties.title === sheetName);
      const targetSheetId = targetSheet?.properties?.sheetId;
      if (typeof targetSheetId === 'number') {
        await googleSheetsService.deleteRow(spreadsheetId, targetSheetId, targetRow);
      } else {
        await googleSheetsService.clearRange(spreadsheetId, `A${targetRow}:C${targetRow}`, sheetName);
      }
      await loadCategories(spreadsheetId, sheetName);
    } catch (err) {
      alert('Failed to delete category: ' + (err?.message || err));
      try { await loadCategories(spreadsheetId, sheetName); } catch (e) { /* ignore */ }
    }
  };

  const groupedCategories = (categories || []).reduce((acc, cat) => {
    if (!acc[cat.category]) acc[cat.category] = [];
    acc[cat.category].push(cat);
    return acc;
  }, {});

  if (isLoading) {
    return (
      <div className="bg-white rounded-xl shadow-lg p-8">
        <div className="flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p className="text-gray-600">Loading categories...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white rounded-xl shadow-lg p-8">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-700">
            <strong>Error:</strong> {error}
          </p>
          <button onClick={findOrCreateSpreadsheetByName} className="mt-3 px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg">Retry</button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {spreadsheetId && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <p className="text-sm text-blue-700">
            <strong>Connected to Categories Sheet:</strong>{' '}
            <a href={`https://docs.google.com/spreadsheets/d/${spreadsheetId}`} target="_blank" rel="noopener noreferrer" className="underline hover:text-blue-900 font-medium">
              Open in Google Sheets
            </a>
          </p>
        </div>
      )}

      <div className="bg-white rounded-xl shadow-lg p-6">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-bold text-gray-800">Manage Categories</h2>
          <button onClick={() => setShowAddForm(!showAddForm)} className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors shadow-md hover:shadow-lg">
            <Plus size={20} />
            {showAddForm ? 'Cancel' : 'Add Category'}
          </button>
        </div>

        {showAddForm && (
          <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-lg p-6 mb-6 border-2 border-blue-300 shadow-md">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">Add New Category</h3>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Category Name <span className="text-red-500">*</span>
                </label>
                <input type="text" value={newCategory.category} onChange={(e) => setNewCategory(prev => ({ ...prev, category: e.target.value }))} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent" placeholder="e.g., FOOD 🥗" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Monthly Budget (optional)
                </label>
                <input type="number" value={newCategory.budget} onChange={(e) => setNewCategory(prev => ({ ...prev, budget: e.target.value }))} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent" placeholder="0.00" step="0.01" min="0" />
              </div>
            </div>

            <div className="space-y-3">
              <div className="flex justify-between items-center">
                <label className="block text-sm font-medium text-gray-700">
                  Subcategories <span className="text-red-500">*</span>
                </label>
                <button onClick={() => setNewCategory(prev => ({ ...prev, subcategories: [...prev.subcategories, { subCategory: '' }] }))} className="flex items-center gap-1 text-sm text-blue-600 hover:text-blue-700 font-medium">
                  <PlusCircle size={16} />
                  Add Another Subcategory
                </button>
              </div>

              {newCategory.subcategories.map((sub, index) => (
                <div key={index} className="flex gap-3 items-start">
                  <div className="flex-1">
                    <input type="text" value={sub.subCategory} onChange={(e) => setNewCategory(prev => ({ ...prev, subcategories: prev.subcategories.map((s, i) => i === index ? { ...s, subCategory: e.target.value } : s) }))} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent" placeholder="e.g., Groceries 🛒" />
                  </div>
                  {newCategory.subcategories.length > 1 && (
                    <button onClick={() => setNewCategory(prev => ({ ...prev, subcategories: prev.subcategories.filter((_, i) => i !== index) }))} className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors" title="Remove subcategory">
                      <MinusCircle size={20} />
                    </button>
                  )}
                </div>
              ))}
            </div>

            <button onClick={handleAddCategory} className="mt-4 px-6 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors font-medium shadow-md hover:shadow-lg">
              Add Category with Subcategories
            </button>
          </div>
        )}

        <div className="space-y-6">
          {Object.keys(groupedCategories).length === 0 ? (
            <div className="text-center py-12 text-gray-500 bg-gray-50 rounded-lg">
              <FolderTree size={48} className="mx-auto mb-4 opacity-50" />
              <p className="font-medium">No categories yet</p>
              <p className="text-sm mt-2">Add your first category to get started!</p>
            </div>
          ) : (
            Object.entries(groupedCategories).map(([categoryName, subcategories]) => {
              const categoryBudget = subcategories.length > 0 ? subcategories[0].budget : 0;

              return (
                <div key={categoryName} className="border border-gray-200 rounded-lg overflow-hidden">
                  <div className="bg-gradient-to-r from-indigo-50 to-blue-50 px-4 py-3 border-b border-gray-200">
                    <div className="flex justify-between items-center">
                      <div className="flex items-center gap-4">
                        <h3 className="text-lg font-bold text-gray-800">{categoryName}</h3>
                        {categoryBudget > 0 && (
                          <span className="px-3 py-1 bg-green-100 text-green-700 rounded-full text-sm font-semibold">
                            Budget: {formatCurrency(categoryBudget, currency, showCurrency, showAmounts)}
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-4">
                        <div className="text-sm text-gray-600">
                          {subcategories.length} subcategor{subcategories.length === 1 ? 'y' : 'ies'}
                        </div>
                        <button onClick={() => handleEditCategoryStart(categoryName)} className="p-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors" title="Edit category">
                          <Edit2 size={18} />
                        </button>
                      </div>
                    </div>
                  </div>

                  {editingCategory === categoryName ? (
                    <div className="p-4 bg-blue-50">
                      <div className="mb-4">
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Monthly Budget (optional)
                        </label>
                        <input
                          type="number"
                          value={editCategoryData.budget}
                          onChange={(e) => setEditCategoryData(prev => ({ ...prev, budget: e.target.value }))}
                          className="w-full md:w-64 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 bg-white"
                          step="0.01"
                          min="0"
                        />
                      </div>

                      <h4 className="font-semibold text-gray-800 mb-3">Subcategories</h4>

                      <div className="space-y-2 mb-4">
                        {editCategoryData.subcategories.map((sub, index) => (
                          <div key={sub.id} className="flex gap-3 items-center bg-white p-2 rounded">
                            <div className="flex-1">
                              <input type="text" value={sub.subCategory} onChange={(e) => updateEditSubcategory(index, 'subCategory', e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500" />
                            </div>
                            <button onClick={() => handleDelete(sub.id)} className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors" title="Delete subcategory">
                              <Trash2 size={18} />
                            </button>
                          </div>
                        ))}
                      </div>

                      {editCategoryData.newSubcategories && editCategoryData.newSubcategories.length > 0 && (
                        <div className="space-y-2 mb-4">
                          <p className="text-sm font-medium text-gray-700">New Subcategories:</p>
                          {editCategoryData.newSubcategories.map((sub, index) => (
                            <div key={index} className="flex gap-3 items-center bg-green-50 p-2 rounded border border-green-200">
                              <div className="flex-1">
                                <input type="text" value={sub.subCategory} onChange={(e) => updateNewEditSubcategory(index, 'subCategory', e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500" placeholder="New subcategory" />
                              </div>
                              <button onClick={() => removeEditSubcategoryField(index)} className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors">
                                <MinusCircle size={20} />
                              </button>
                            </div>
                          ))}
                        </div>
                      )}

                      <div className="flex gap-3">
                        <button onClick={addEditSubcategoryField} className="flex items-center gap-1 px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors text-sm">
                          <PlusCircle size={16} />
                          Add Subcategory
                        </button>
                        <button onClick={handleSaveCategoryEdit} className="flex items-center gap-1 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors text-sm">
                          <Save size={16} />
                          Save Changes
                        </button>
                        <button onClick={() => { setEditingCategory(null); setEditCategoryData({ budget: '', subcategories: [], newSubcategories: [] }); }} className="flex items-center gap-1 px-4 py-2 bg-gray-300 hover:bg-gray-400 text-gray-700 rounded-lg transition-colors text-sm">
                          <X size={16} />
                          Cancel
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="divide-y divide-gray-100">
                      {subcategories.map((cat) => (
                        <div key={cat.id} className="px-4 py-3 bg-white hover:bg-gray-50 transition-colors">
                          <div className="flex justify-between items-center">
                            <div className="flex-1">
                              <p className="text-gray-800 font-medium">{cat.subCategory}</p>
                            </div>
                            <div className="flex items-center gap-2">
                              <button onClick={() => handleDelete(cat.id)} className="p-2 bg-red-100 hover:bg-red-200 text-red-700 rounded-lg transition-colors" title="Delete">
                                <Trash2 size={18} />
                              </button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>

        {categories.length > 0 && (
          <div className="mt-6 p-4 bg-gradient-to-r from-green-50 to-blue-50 border border-green-200 rounded-lg">
            <div className="flex justify-between items-center">
              <div>
                <p className="text-sm text-gray-600">Total Subcategories</p>
                <p className="text-2xl font-bold text-gray-800">{categories.length}</p>
              </div>
              <div className="text-center">
                <p className="text-sm text-gray-600">Main Categories</p>
                <p className="text-2xl font-bold text-gray-800">{Object.keys(groupedCategories).length}</p>
              </div>
              <div className="text-right">
                <p className="text-sm text-gray-600">Total Monthly Budget</p>
                <p className="text-2xl font-bold text-green-600">
                  {formatCurrency(Object.entries(groupedCategories).reduce((sum, [_, subs]) => sum + (subs[0]?.budget || 0), 0), currency, showCurrency, showAmounts)}
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CategoriesManager;
